import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import api, { setToken } from "@/lib/api";
import { profileCountries, profileCities, nearCountries } from "@/lib/places";
import { User, Building2, Camera, Fingerprint } from "lucide-react";
import { startAuthentication } from "@simplewebauthn/browser";
import { toast } from "sonner";
import { PolicyDialog } from "@/components/PolicyDialog";

const empty = { name: "", surname: "", email: "", password: "", password2: "", country: "", city: "", address: "" };

export function AuthDialog() {
  const { authOpen, setAuthOpen, login, register, setUser, errMsg } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [acc, setAcc] = useState("private");
  const [form, setForm] = useState(empty);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPrev, setAvatarPrev] = useState("");
  const [preset, setPreset] = useState("");
  const [terms, setTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);

  const reset = () => { setForm(empty); setAvatarFile(null); setAvatarPrev(""); setPreset(""); setTerms(false); setError(""); setSent(false); };
  const close = () => { setAuthOpen(false); reset(); setMode("login"); };

  const pickAvatar = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setAvatarFile(f);
    setAvatarPrev(URL.createObjectURL(f));
    setPreset("");
  };

  const uploadAvatar = async () => {
    const fd = new FormData();
    fd.append("file", avatarFile);
    const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data.url;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (mode === "forgot") {
      setLoading(true);
      try { await api.post("/auth/forgot-password", { email: form.email }); setSent(true); }
      catch (e2) { setError(errMsg(e2)); }
      finally { setLoading(false); }
      return;
    }

    if (mode === "register") {
      if (!avatarFile && !preset) { setError("Zgjedh një foto profili ose një nga figurat"); return; }
      if (form.password !== form.password2) { setError("Fjalëkalimet nuk përputhen"); return; }
      if (!terms) { setError("Prano Kushtet dhe Politikën e Privatësisë"); return; }
    }

    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Mirë se erdhe!");
      } else {
        const payload = {
          name: form.name, surname: form.surname, email: form.email, password: form.password,
          account_type: acc, country: form.country, city: form.city, address: form.address,
        };
        await register(payload);
        try {
          const url = avatarFile ? await uploadAvatar() : preset;
          const r = await api.put("/auth/me", { avatar: url });
          setUser(r.data);
        } catch { /* avatar optional fallback */ }
        toast.success("Llogaria u krijua! Mirë se erdhe.");
        close();
        nav("/profil");
        return;
      }
      close();
    } catch (e2) { setError(errMsg(e2)); }
    finally { setLoading(false); }
  };

  const cities = profileCities(form.country);
  const inp = "rounded-xl h-11";

  const bioLogin = async () => {
    setError("");
    if (!form.email) { setError("Shkruaj email-in për të hyrë me biometrikë"); return; }
    setLoading(true);
    try {
      const opts = (await api.post("/webauthn/login/begin", { email: form.email })).data;
      const asr = await startAuthentication({ optionsJSON: opts });
      const r = await api.post("/webauthn/login/complete", { email: form.email, credential: asr });
      if (r.data.token) setToken(r.data.token);
      setUser(r.data.user);
      toast.success("Mirë se erdhe!");
      close();
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Hyrja me biometrikë dështoi");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={authOpen} onOpenChange={(o) => (o ? setAuthOpen(true) : close())}>
      <DialogContent className="max-w-sm rounded-3xl max-h-[88vh] overflow-y-auto" data-testid="auth-dialog">
        <DialogHeader>
          <div className="flex justify-center mb-1"><Logo /></div>
          <DialogTitle className="text-center text-xl">
            {mode === "forgot" ? "Harrove fjalëkalimin?" : "Mirë se erdhe në GjejShqip"}
          </DialogTitle>
          <p className="text-center text-sm text-slate-500">
            {mode === "register" ? "Bëhu pjesë e komunitetit shqiptar në diasporë." : mode === "forgot" ? "Shkruaj email-in tënd për të rivendosur fjalëkalimin." : "Gjej çka të duhet."}
          </p>
        </DialogHeader>

        {mode === "forgot" && sent ? (
          <div className="text-center py-4 space-y-3">
            <p className="text-sm text-slate-600">Nëse ky email ekziston, të dërguam një link për rivendosjen e fjalëkalimit. Kontrollo kutinë tënde.</p>
            <Button data-testid="forgot-back-login" onClick={() => { setMode("login"); setSent(false); }} className="w-full h-11 rounded-xl bg-emerald-500 hover:bg-emerald-600">Kthehu te hyrja</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3 mt-1">
            {mode === "register" && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" data-testid="acc-private" onClick={() => setAcc("private")} className={`flex flex-col items-center gap-1 rounded-xl border-2 p-3 transition-colors ${acc === "private" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-500"}`}>
                    <User className="w-5 h-5" /><span className="text-xs font-semibold">Privat</span>
                  </button>
                  <button type="button" data-testid="acc-business" onClick={() => setAcc("business")} className={`flex flex-col items-center gap-1 rounded-xl border-2 p-3 transition-colors ${acc === "business" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-500"}`}>
                    <Building2 className="w-5 h-5" /><span className="text-xs font-semibold">Biznis</span>
                  </button>
                </div>
                <p className="text-[11px] text-slate-400 text-center -mt-1">{acc === "business" ? "Llogaritë e biznesit mund të publikojnë shpallje." : "Llogaritë private nuk publikojnë shpallje."}</p>

                <div className="flex justify-center items-center gap-3">
                  {["male", "female"].map((g) => (
                    <button key={g} type="button" data-testid={`auth-avatar-${g}`} onClick={() => { setPreset(`/avatars/${g}.png`); setAvatarFile(null); setAvatarPrev(""); }} className={`w-16 h-16 rounded-full overflow-hidden transition-transform ${preset === `/avatars/${g}.png` ? "ring-4 ring-emerald-500 scale-110" : "opacity-70"}`}>
                      <img src={`/avatars/${g}.png`} alt={g === "male" ? "Burrë" : "Grua"} className="w-full h-full object-cover" />
                    </button>
                  ))}
                  <label className="relative cursor-pointer" data-testid="auth-avatar-label">
                    <div className={`w-20 h-20 rounded-full bg-slate-100 border-2 border-dashed flex items-center justify-center overflow-hidden ${avatarPrev ? "border-emerald-500" : "border-slate-300"}`}>
                      {avatarPrev ? <img src={avatarPrev} alt="" className="w-full h-full object-cover" /> : <Camera className="w-6 h-6 text-slate-400" />}
                    </div>
                    <span className="absolute -bottom-1 -right-1 bg-emerald-500 text-white rounded-full p-1.5"><Camera className="w-3.5 h-3.5" /></span>
                    <input data-testid="auth-avatar-input" type="file" accept="image/*" onChange={pickAvatar} className="hidden" />
                  </label>
                </div>
                <p className="text-[11px] text-slate-400 text-center -mt-1">Zgjidh një figurë ose ngarko foton tënde</p>

                <div className="grid grid-cols-2 gap-2">
                  <Input data-testid="auth-name-input" placeholder="Emri" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className={inp} />
                  <Input data-testid="auth-surname-input" placeholder="Mbiemri" value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} required className={inp} />
                </div>
              </>
            )}

            <Input data-testid="auth-email-input" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className={inp} />

            {mode !== "forgot" && (
              <Input data-testid="auth-password-input" type="password" placeholder="Fjalëkalimi" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className={inp} />
            )}

            {mode === "register" && (
              <>
                <Input data-testid="auth-password2-input" type="password" placeholder="Konfirmo fjalëkalimin" value={form.password2} onChange={(e) => setForm({ ...form, password2: e.target.value })} required className={inp} />
                <select data-testid="auth-country-input" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value, city: "" })} required className={`${inp} w-full border border-slate-200 rounded-xl px-3 bg-white text-sm`}>
                  <option value="">Zgjedh shtetin</option>
                  {nearCountries(profileCountries()).map((c) => <option key={c.name} value={c.name}>{c.flag} {c.name}</option>)}
                </select>
                <select data-testid="auth-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required disabled={!form.country} className={`${inp} w-full border border-slate-200 rounded-xl px-3 bg-white text-sm disabled:opacity-50`}>
                  <option value="">{form.country ? "Zgjedh qytetin" : "Zgjedh shtetin më parë"}</option>
                  {cities.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <Input data-testid="auth-address-input" placeholder="Adresa" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} required className={inp} />
                <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
                  <input data-testid="auth-terms-checkbox" type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-0.5 w-4 h-4 accent-emerald-500" />
                  <span>Pranoj <button type="button" data-testid="auth-read-policy" onClick={(e) => { e.preventDefault(); setPolicyOpen(true); }} className="text-emerald-600 font-semibold underline">Kushtet e Përdorimit dhe Politikën e Privatësisë</button>.</span>
                </label>
                <PolicyDialog open={policyOpen} onClose={() => setPolicyOpen(false)} onAccept={() => { setTerms(true); setError(""); }} />
              </>
            )}

            {mode === "login" && (
              <button type="button" data-testid="auth-forgot-link" onClick={() => { setMode("forgot"); setError(""); }} className="text-xs text-emerald-600 font-semibold">Harrove fjalëkalimin?</button>
            )}

            {error && <p className="text-sm text-red-500 text-center">{error}</p>}
            <Button data-testid="auth-submit-button" type="submit" disabled={loading} className="w-full h-11 rounded-xl bg-emerald-500 hover:bg-emerald-600 font-semibold">
              {loading ? "Duke pritur..." : mode === "login" ? "Kyçu" : mode === "register" ? "Krijo llogarinë" : "Dërgo linkun"}
            </Button>
            {mode === "login" && (
              <button type="button" data-testid="biometric-login" onClick={bioLogin} disabled={loading} className="w-full h-11 rounded-xl border-2 border-emerald-500 text-emerald-600 font-semibold flex items-center justify-center gap-2">
                <Fingerprint className="w-5 h-5" />Hyr me gisht / fytyrë
              </button>
            )}
          </form>
        )}

        {mode === "forgot" && !sent && (
          <button data-testid="forgot-back" onClick={() => { setMode("login"); setError(""); }} className="text-sm text-center text-slate-500 mt-1 hover:text-emerald-600">Kthehu te hyrja</button>
        )}
        {mode !== "forgot" && (
          <button
            data-testid="auth-toggle-mode"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="text-sm text-center text-slate-500 mt-1 hover:text-emerald-600"
          >
            {mode === "login" ? "Nuk ke llogari? Regjistrohu" : "Ke llogari? Kyçu"}
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default AuthDialog;
